/**
 * API Error Handler
 * Centralized error handling for all API calls
 */

import type { AxiosError } from 'axios';

export interface ApiErrorResponse {
  success: false;
  error: {
    message: string;
    code: string;
    field?: string;
    details?: Record<string, any>;
  };
  meta?: {
    timestamp: string;
    path?: string;
    requestId?: string;
  };
}

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly code: string;
  public readonly field?: string;
  public readonly details?: Record<string, any>;

  constructor(
    message: string,
    statusCode: number,
    code: string,
    field?: string,
    details?: Record<string, any>
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.field = field;
    this.details = details;
  }

  /**
   * Create ApiError from Axios error
   */
  static fromAxiosError(error: AxiosError<ApiErrorResponse>): ApiError {
    const status = error.response?.status ?? 500;
    const errorData = error.response?.data?.error;

    return new ApiError(
      errorData?.message || error.message,
      status,
      errorData?.code || 'UNKNOWN_ERROR',
      errorData?.field,
      errorData?.details
    );
  }

  /**
   * Check if error is due to unauthorized access
   */
  isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  /**
   * Check if error is due to forbidden access
   */
  isForbidden(): boolean {
    return this.statusCode === 403;
  }

  /**
   * Check if error is validation error
   */
  isValidationError(): boolean {
    return this.statusCode === 400;
  }

  /**
   * Check if error is conflict
   */
  isConflict(): boolean {
    return this.statusCode === 409;
  }

  /**
   * Check if error is not found
   */
  isNotFound(): boolean {
    return this.statusCode === 404;
  }
}

/**
 * Handle API errors
 */
export const handleApiError = (error: unknown): never => {
  if (error instanceof ApiError) {
    throw error;
  }

  if (error instanceof Error) {
    if ('response' in error && error.response) {
      throw ApiError.fromAxiosError(error as AxiosError<ApiErrorResponse>);
    }
    throw new ApiError(error.message, 500, 'UNKNOWN_ERROR');
  }

  throw new ApiError('An unknown error occurred', 500, 'UNKNOWN_ERROR');
};
